// eslint-disable-next-line no-use-before-define
import * as React from 'react';

import {
  ProjectSearchSortDirection,
  SortBy as ProjectSearchSortBy
} from '../../../../clients/shared/ProjectSearch';
import { Application, getApplication } from '../../models/Application';

export type ProjectSortScope = 'kanban' | 'triage' | 'archive';

export type ProjectSortKey =
  | 'position'
  | 'priority_id'
  | 'due_at'
  | 'created_at'
  | 'assignee_ids'
  | 'updated_at';

export const PROJECT_SORT_OPTIONS: {
  key: ProjectSortKey;
  label: string;
}[] = [
  { key: 'position', label: 'Manually' },
  { key: 'priority_id', label: 'Severity' },
  { key: 'due_at', label: 'Due date' },
  { key: 'created_at', label: 'Date created' },
  { key: 'assignee_ids', label: 'Assigned to' },
  { key: 'updated_at', label: 'Last modified' }
];

export const PROJECT_SORT_DIRECTIONS: Partial<Record<
  ProjectSortKey,
  ProjectSearchSortDirection[]
>> = {
  priority_id: [
    { ascending: false, arrow: '↑', label: 'High first' },
    { ascending: true, arrow: '↓', label: 'Low first' }
  ],
  due_at: [
    { ascending: true, arrow: '↑', label: 'Soonest' },
    { ascending: false, arrow: '↓', label: 'Latest' }
  ],
  created_at: [
    { ascending: true, arrow: '↑', label: 'Newest' },
    { ascending: false, arrow: '↓', label: 'Oldest' }
  ],
  assignee_ids: [
    { ascending: true, arrow: '↑', label: 'A – Z' },
    { ascending: false, arrow: '↓', label: 'Z – A' }
  ],
  updated_at: [
    { ascending: true, arrow: '↑', label: 'Newest' },
    { ascending: false, arrow: '↓', label: 'Oldest' }
  ]
};

const DEFAULT_PROJECT_SORT: Record<ProjectSortScope, ProjectSortKey> = {
  kanban: 'position',
  triage: 'created_at',
  archive: 'updated_at'
};

export const getProjectSortScope = (mode?: string): ProjectSortScope =>
  mode === 'triage' || mode === 'archive' ? mode : 'kanban';

export const getProjectSortOptions = (scope: ProjectSortScope) =>
  scope === 'kanban'
    ? PROJECT_SORT_OPTIONS
    : PROJECT_SORT_OPTIONS.filter(({ key }) => key !== 'position');

export const getCurrentProjectSort = (
  application: Application,
  scope: ProjectSortScope
): ProjectSortKey => {
  const currentSort = application?.get(`${scope}_sortAttribute`);
  const availableOptions = getProjectSortOptions(scope);

  return availableOptions.some(({ key }) => key === currentSort)
    ? currentSort
    : DEFAULT_PROJECT_SORT[scope];
};

export const getCurrentProjectSortAscending = (
  application: Application,
  scope: ProjectSortScope
) => application?.get(`${scope}_sortAscending`) !== false;

export const applyProjectSort = (
  application: Application,
  scope: ProjectSortScope,
  sort: ProjectSortKey
) => {
  const sortAscending = getCurrentProjectSortAscending(application, scope);
  const setSort = application?.get('setSort');
  if (typeof setSort !== 'function') return sortAscending;

  setSort(sort);

  if (sort === 'priority_id' && sortAscending) {
    const setOrder = application?.get('setOrder');
    if (typeof setOrder === 'function') {
      setOrder();
      return false;
    }
  }

  return sortAscending;
};

export const applyProjectSortDirection = (
  application: Application,
  scope: ProjectSortScope,
  ascending: boolean
) => {
  const currentAscending = getCurrentProjectSortAscending(application, scope);
  if (currentAscending === ascending) return currentAscending;

  const setOrder = application?.get('setOrder');
  if (typeof setOrder !== 'function') return currentAscending;

  setOrder();
  return ascending;
};

const getActiveScope = () => {
  const mode =
    typeof window === 'undefined'
      ? undefined
      : window.bugherdStore?.getState?.()?.mode;

  return getProjectSortScope(mode);
};

const SortBy: React.FC = () => {
  const application = getApplication();
  const initialScope = getActiveScope();
  const [isOpen, setIsOpen] = React.useState(false);
  const [scope, setScope] = React.useState(initialScope);
  const [sort, setSort] = React.useState(() =>
    getCurrentProjectSort(application, initialScope)
  );
  const [sortAscending, setSortAscending] = React.useState(() =>
    getCurrentProjectSortAscending(application, initialScope)
  );

  const handleOpenChange = (open: boolean) => {
    if (open) {
      const activeScope = getActiveScope();
      setScope(activeScope);
      setSort(getCurrentProjectSort(application, activeScope));
      setSortAscending(
        getCurrentProjectSortAscending(application, activeScope)
      );
    }
    setIsOpen(open);
  };

  const handleSortChange = (nextSort: ProjectSortKey) => {
    setSortAscending(applyProjectSort(application, scope, nextSort));
    setSort(nextSort);
  };

  const handleDirectionChange = (ascending: boolean) =>
    setSortAscending(applyProjectSortDirection(application, scope, ascending));

  return (
    <ProjectSearchSortBy
      directions={PROJECT_SORT_DIRECTIONS}
      manualSortHint="Cards keep the order you drag them into."
      onDirectionChange={handleDirectionChange}
      onOpenChange={handleOpenChange}
      open={isOpen}
      onSortChange={handleSortChange}
      options={getProjectSortOptions(scope)}
      sort={sort}
      sortAscending={sortAscending}
    />
  );
};

export default SortBy;
